// Spine Lancer (replaces `lancer`) — lean slate-armoured soldier with a forward-pointing helmet, narrow waist and a very
// long split-blade spear carried ahead of the body (the key silhouette). Wind-up: the upper body pitches forward about
// the hips — shoulders drop and the spear comes down level into the charge lane.
// Elite (Crown Piercer): longer split spear with barbed blades and heavy armoured charging shoulders.
import * as THREE from 'three/webgpu';
import { ArtBuilder, at, EnemyArt, limb, PAL, plate } from './kit';
import { ball, compassDip, leg, noSkateSwing, slab, V3 } from './parts_spec';

const STRIDE = 3.0, HIP = 1.05;
const SW = noSkateSwing(HIP, STRIDE);
const SLATE = 0x56607a, SLATE_DARK = 0x3a4256, EDGE = 0xff2f6a;
/** spear rest angle above horizontal; the wind-up rotates the upper body + spear forward by BRACE */
const REST = 0.24, BRACE = 0.3;

function build(elite: boolean): ReturnType<ArtBuilder['build']> {
  const b = new ArtBuilder();
  const sw = elite ? noSkateSwing(HIP * 1.7, STRIDE) : SW;
  for (const s of [-1, 1]) {
    leg(b, {
      hip: [s * 0.11, HIP, 0], knee: [s * 0.12, 0.56, 0.08], ankle: [s * 0.11, 0.1, -0.01],
      rThigh: 0.08, rShin: 0.065, foot: [0.13, 0.08, 0.32], toe: 0.07,
      thigh: PAL.iron, shin: SLATE, kneeCol: PAL.ivory, footCol: SLATE_DARK,
      swing: sw, sphase: s > 0 ? 0 : Math.PI, guard: 0.1,
    });
  }
  // hips (static) — fauld plates
  b.add(at(plate(0.32, 0.16, 0.22, 0.04, 1), 0, 1.04, 0), { color: SLATE_DARK });
  b.add(at(plate(0.16, 0.2, 0.04, 0.015, 1), 0, 0.94, 0.12, -0.1), { color: PAL.ivoryShade });

  // upper body: everything above the waist rotates about the hip on the wind-up
  const U = { pivot: [0, HIP, 0] as V3, raise: BRACE };
  b.add(limb([0, 1.08, 0], [0, 1.3, 0], 0.12, 0.1, 8), { color: PAL.iron, ...U }); // narrow waist
  b.add(limb([0, 1.28, 0], [0, 1.64, 0], 0.13, 0.25, 8), { color: SLATE, ...U });  // wedge chest
  b.add(at(plate(0.3, 0.26, 0.07, 0.03, 1), 0, 1.5, 0.15, -0.12), { color: PAL.ivory, ...U });
  b.add(at(plate(0.04, 0.18, 0.02, 0.01, 1), 0, 1.5, 0.19, -0.12), { color: EDGE, ...U });
  b.add(limb([0, 1.62, 0], [0, 1.76, 0.02], 0.06, 0.055), { color: PAL.iron, ...U });
  // forward-pointing helmet with glowing visor slits and a back fin
  b.add(ball(0.135, [0, 1.87, -0.02], [1, 1.05, 1.1], 10, 7), { color: SLATE, ...U });
  b.add(at(new THREE.ConeGeometry(0.12, 0.36, 8), 0, 1.86, 0.17, Math.PI / 2, 0, 0, [1, 1, 0.75]), { color: SLATE, ...U });
  b.add(at(plate(0.03, 0.1, 0.34, 0.012, 1), 0, 1.99, -0.04, 0.12), { color: PAL.ivory, ...U });
  for (const s of [-1, 1]) b.add(at(plate(0.07, 0.018, 0.02, 0.006, 1), s * 0.07, 1.88, 0.12, 0, s * 0.55), { color: EDGE, glow: true, ...U });
  // shoulders
  const pw = elite ? 0.32 : 0.2, ph = elite ? 0.17 : 0.11, pd = elite ? 0.38 : 0.24;
  for (const s of [-1, 1]) {
    b.add(at(plate(pw, ph, pd, 0.04, 1), s * (0.26 + (elite ? 0.04 : 0)), 1.64, elite ? 0.04 : 0, elite ? -0.2 : 0, 0, s * -0.42), { color: SLATE_DARK, ...U });
    b.add(at(plate(pw * 0.8, 0.05, pd * 0.85, 0.02, 1), s * (0.28 + (elite ? 0.05 : 0)), 1.71 + (elite ? 0.04 : 0), elite ? 0.04 : 0, elite ? -0.2 : 0, 0, s * -0.42), { color: PAL.ivory, ...U });
  }
  if (elite) {
    // armoured charging shoulders: forward rams and a raised gorget
    for (const s of [-1, 1]) b.add(at(new THREE.ConeGeometry(0.07, 0.3, 6), s * 0.33, 1.68, 0.26, Math.PI / 2 - 0.2), { color: PAL.ivory, ...U });
    b.add(at(plate(0.36, 0.14, 0.08, 0.03, 1), 0, 1.7, -0.1, -0.3), { color: SLATE_DARK, ...U });
  }

  // spear along x = SX, rising at REST above horizontal
  const SX = -0.19, d = [0, Math.sin(REST), Math.cos(REST)], P0: V3 = [SX, 1.25, 0.02];
  const P = (t: number, dx = 0, up = 0): V3 => [P0[0] + dx, P0[1] + d[1] * t + d[2] * up, P0[2] + d[2] * t - d[1] * up];
  const tEnd = elite ? 2.9 : 2.45, blade = elite ? 1.05 : 0.8, butt = -0.75;
  b.add(limb(P(butt), P(tEnd), 0.035, 0.03, 6), { color: PAL.iron, ...U });
  b.add(limb(P(butt - 0.08), P(butt), 0.02, 0.045, 6), { color: PAL.copper, ...U });
  b.add(limb(P(tEnd - 0.05), P(tEnd + 0.06), 0.06, 0.05, 6), { color: PAL.copper, ...U });
  b.add(limb(P(0.9), P(1.15), 0.045, 0.045, 6), { color: SLATE_DARK, ...U }); // grip wrap
  // split blade: two broad flat blades side by side, a gap between them; red-magenta inner edges glow
  for (const s of [-1, 1]) {
    const gap = 0.055, w = elite ? 0.11 : 0.09;
    const base = P(tEnd + 0.04, s * (gap + w * 0.5)), tip = P(tEnd + blade, s * (gap * 0.6 + w * 0.15));
    b.add(slab(base, tip, w, 0.03, 0.012), { color: PAL.ivory, ...U });
    b.add(at(new THREE.ConeGeometry(w * 0.55, 0.22, 4), 0, 0, 0, 0, 0, 0, [1, 1, 0.3]).rotateX(Math.PI / 2 - REST).translate(tip[0], tip[1] + d[1] * 0.1, tip[2] + d[2] * 0.1), { color: PAL.ivory, ...U });
    b.add(limb(P(tEnd + 0.06, s * gap), P(tEnd + blade + 0.04, s * gap * 0.6), 0.012, 0.008, 4), { color: EDGE, glow: true, ...U });
    if (elite) {
      // outward barbs
      b.add(slab(P(tEnd + 0.15, s * (gap + w)), P(tEnd + 0.45, s * (gap + w + 0.16)), 0.05, 0.03, 0.01), { color: PAL.ivory, ...U });
      b.add(slab(P(tEnd + 0.15, s * (gap + w + 0.02), 0.0), P(tEnd + 0.4, s * (gap + w + 0.15), 0.0), 0.012, 0.035, 0.005), { color: EDGE, glow: true, ...U });
    }
  }
  // arms gripping the spear (right hand low by the hip, left hand forward), rigid with the upper body
  const rh = P(0.05, 0.02), lh = P(0.62, 0.03);
  const arm = (sh: V3, el: V3, hand: V3) => {
    b.add(limb(sh, el, 0.06, 0.055), { color: SLATE_DARK, ...U });
    b.add(limb(el, hand, 0.055, 0.045), { color: PAL.iron, ...U });
    b.add(ball(0.06, el, [1, 1, 1], 8, 6), { color: PAL.copper, ...U });
    b.add(ball(0.055, hand, [1, 1, 1], 8, 6), { color: PAL.ivoryShade, ...U });
  };
  arm([-0.27, 1.6, 0], [-0.32, 1.36, -0.12], rh);
  arm([0.27, 1.6, 0], [0.2, 1.38, 0.3], lh);
  return b.build();
}

export const ART: EnemyArt = { name: 'Spine Lancer', build, stride: STRIDE, bob: compassDip(HIP, SW) * 0.85 };
